import {acceptHMRUpdate, defineStore} from 'pinia'

import {ref, computed} from 'vue'
import axios from 'axios'
import FormProperty from 'src/classes/FormProperty.js'
import {useAuthStore} from 'src/stores/AuthStore.js'

// You can name the return value of `defineStore()` anything you want,
// but it's best to use the name of the store and surround it with `use`
// and `Store` (e.g. `useUserStore`, `useCartStore`, `useProductStore`)
// the first argument is a unique id of the store across your application
export const useFormularStore = defineStore('formular', () => {
  // State
  const objClassID = ref('')
  const formularList = ref([])
  const formularClass = ref({})
  const pluginList = ref([])
  const fileUploadPromises = ref([])
  const pendingOperations = ref(0)
  const pendingRequests = ref(new Map());

  // Timeout-Zeit in Millisekunden (1 Minute = 60000 ms)
  const REQUEST_TIMEOUT = 30000;

  // Generiert eine eindeutige ID für jede Anfrage
  function generateRequestId() {
    return Date.now() + '_' + Math.random().toString(36).substring(2, 9);
  }

  // Startet eine Anfrage mit Timeout
  function startRequest() {
    const requestId = generateRequestId();
    incrementPendingOperations();

    // Timeout einrichten
    const timeoutId = setTimeout(() => {
      // Wenn der Timeout ausgelöst wird, diese Anfrage als beendet markieren
      if (pendingRequests.value.has(requestId)) {
        console.warn(`Request ${requestId} timed out after ${REQUEST_TIMEOUT / 1000} seconds`);
        pendingRequests.value.delete(requestId);
        decrementPendingOperations();
      }
    }, REQUEST_TIMEOUT);

    // Anfrage speichern
    pendingRequests.value.set(requestId, timeoutId);

    return requestId;
  }

  // Beendet eine Anfrage
  function endRequest(requestId) {
    if (pendingRequests.value.has(requestId)) {
      // Timeout löschen
      clearTimeout(pendingRequests.value.get(requestId));
      pendingRequests.value.delete(requestId);
      decrementPendingOperations();
    }
  }

  function clear() {
    formularList.value = []
    formularClass.value = {}
    pluginList.value = []
    fileUploadPromises.value = []
  }

  function incrementPendingOperations() {
    pendingOperations.value++
    // console.log('INCrementPendingOperations: ', pendingOperations)
  }

  function decrementPendingOperations() {
    pendingOperations.value--
    // console.log('DEcrementPendingOperations: ', pendingOperations)
  }

  const getPendingOperations = computed(() => pendingOperations.value)

  // Actions
  async function formularListGet({objClassID, objInstanceID, parentForm, activeFormularList, preFillParameterList}) {
    const authStore = useAuthStore()
    try {
      await authStore.sessionTouch()
      const response = await axios.post('/formularListGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        objClassID: objClassID,
        objInstanceID: objInstanceID,
        parentForm: parentForm,
        activeFormularList: activeFormularList,
        preFillParameterList: preFillParameterList,
        loginname: authStore.loginname
      })
      formularList.value = response.data.formularList
      formularClass.value = response.data.formularClass
      pluginList.value = response.data.pluginList
    } catch (e) {
      console.error('formularListGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  function persistFormularList({formularList, objClassID, formularValid, activeFormularList}) {
    const authStore = useAuthStore()
    return new Promise((resolve, reject) => {
      authStore.sessionTouch().then((token) => {
        axios({
          url: "/persistFormularList",
          method: 'POST',
          responseType: 'blob',
          data: {
            sessionToken: authStore.sessionToken,
            siteID: authStore.siteID,
            formularList: formularList,
            objClassID: objClassID,
            loginname: authStore.loginname,
            formularValid,
            activeFormularList
          }
        })
          .then(response => {
            if (response.data.type && response.data.type === "application/octet-stream") {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = "/statics/pdfjs/web/viewer.html?file=" + encodeURIComponent(url);
              link.target = "_blank"
              //let filename = response.headers["content-disposition"].substr(20);
              //link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              // create result json for viewing the status - like it was a normal plugin exec call
              let jsonresp = JSON.stringify({iResult: 1, sOutput: 'Download abgeschlossen', sErrorMsg: ''});
              response.data = new Blob([jsonresp], {type: "application/octet-stream"})
              resolve(response)
            } else {
              let reader = new FileReader();
              reader.onload = function () {
                let resp = JSON.parse(reader.result)
                let result = {data: resp}
                if (resp.iResult !== -1) {
                  resolve(result)
                } else {
                  reject(result)
                }
              }
              reader.readAsText(response.data)
            }
          })
          .catch(error => {
            let reader = new FileReader();
            reader.onload = function () {
              let resp = JSON.parse(reader.result)
              console.error("persistFormularList Error: " + resp.sErrorMsg + ", iResult: " + resp.iResult)
              let result = {response: {data: resp}}
              reject(result)
            }
            reader.readAsText(error.response.data)
          })
      }).catch(error => reject(error))
    })
  }

  async function isPreSafeCheckOK({propertyClassID, objClassID, propertyInstanceValue, formularList}) {
    const authStore = useAuthStore()
    try {
      await authStore.sessionTouch()
      let presafeList = FormProperty.formularListDeepCopy(formularList)
      presafeList[0].formularFields.formularFlatList = []
      let formularFlatList = formularList[0].formularFields.formularFlatList
      let tmpFieldList = []
      for (let k = 0; k < formularFlatList.length; k++) {
        let field = formularFlatList[k]
        let tmpField = FormProperty.createBackendFormProperty(field)
        tmpFieldList.push(tmpField)
      }
      presafeList[0].formularFields.formularFlatList = tmpFieldList

      console.log('...execute the preSafe. formularList: ', presafeList)

      const response = await axios.post('/isPreSafeCheckOK', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        propertyClassID: propertyClassID,
        objClassID: objClassID,
        propertyInstanceValue: propertyInstanceValue,
        formularList: presafeList
      })
      formularList.value = response.data.formularList
      return response
    } catch (e) {
      console.error('isPreSafeCheckOK Error: ' + e)
      throw e
    }
  }

  async function compValueChange(
    {fieldKeyValueList, sourceChangedNativename, propertyTitle, propertyClassID, objClassID, activeFormularList}
  ) {
    const authStore = useAuthStore()
    // GET TOKEN START
    // This is  needed due to performance reasons
    // When authStore.sessionTouch is called, the round trip takes about >>1000<< TIMES longer
    let tokenNotExpired
    if (authStore.sessionToken != null) {
      const currentDate = (
        Math.floor((
          new Date()
        ).valueOf() / 1000 + 150)
      ) // 2:30 minutes offset to make sure there is no overlap
      const expDate = (
        JSON.parse(atob(authStore.sessionToken.split('.')[1]))
      ).exp
      tokenNotExpired = currentDate < expDate
    } else {
      tokenNotExpired = false
    }
    if (!tokenNotExpired) {
      const response = await axios.post('/getSessionToken', {
        withCredentials: true
      })
      authStore.sessionToken = response.data.token
    }
    // GET TOKEN END
    try {
      await authStore.sessionTouch()
      const response = await axios.post('/compValueChange' + '?' + propertyTitle + ';' + sourceChangedNativename, {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        fieldKeyValueList: fieldKeyValueList,
        objClassID: objClassID,
        propertyClassID: propertyClassID,
        activeFormularList: activeFormularList
      })
      const data = response.data
      return data
    } catch (error) {
      console.error(
        'Error compValueChange: ' + error.response.data.sErrorMsg + ', iResult: ' + error.response.data.iResult)
      return undefined
    }
  }

  async function valueListSQLChange({fieldKeyValueList, propertyClassID, objClassID}) {
    const authStore = useAuthStore()
    try {
      await authStore.sessionTouch()
      const response = await axios.post('/valueListSQLChange', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        fieldKeyValueList: fieldKeyValueList,
        objClassID: objClassID,
        propertyClassID: propertyClassID
      })
      return response.data
    } catch (e) {
      console.warn('valueListSQLChange Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  function ticketRangeGet({objInstanceID}) {
    const authStore = useAuthStore()
    return new Promise((resolve, reject) => {
      authStore.sessionTouch()
        .then(() => {
          axios.post('/ticketRangeGet', {
            sessionToken: authStore.sessionToken,
            siteID: authStore.siteID,
            objInstanceID: objInstanceID
          })
            .then(resp => {
              resolve(resp.data)
            })
            .catch(error => {
              console.log(
                'ticketRangeGet Error: ' + error.response.data.sErrorMsg + ', iResult: ' + error.response.data.iResult)
              reject(error)
            })
        })
        .catch(error => reject(error))
    })
  }

  async function executeAutocompleteQuery({propertyClassID, objClassID, searchString, fieldKeyValueList}) {
    try {
      const authStore = useAuthStore()

      await authStore.sessionTouch()

      const response = await axios.post('/executeAutocompleteQuery', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        propertyClassID,
        objClassID,
        searchString,
        fieldKeyValueList
      })

      return response.data

    } catch (error) {
      console.error('executeAutocompleteQuery Error:', error?.response?.data?.sErrorMsg || error.message)
      throw error
    }
  }

  return {
    objClassID,
    formularList,
    formularClass,
    pluginList,
    fileUploadPromises,

    clear,
    formularListGet,
    persistFormularList,
    isPreSafeCheckOK,
    compValueChange,
    executeAutocompleteQuery,

    pendingOperations,
    startRequest,
    endRequest,
    // incrementPendingOperations,
    // decrementPendingOperations,
    getPendingOperations
  }
})

// Enable Hot Module Replacement for stores. make sure to pass the right store definition, `useFormStore` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFormularStore, import.meta.hot))
}
